import { useCallback, useEffect, useRef, useState } from 'react'
import {
  useAddDomain,
  useAutoVerifyDomain,
  useDeleteDomain,
  useDomainDetail,
  useDomains,
} from '@/api/hooks/use-domains'
import { DomainVerifyState } from './domain-verify-state'

interface StepDomainFullProps {
  onNext: () => void
  onSkip: () => void
}

const SESSION_KEY = 'oe_onboarding_domain_id'

/**
 * Extracts the existing_domain_id from an ApiClientError.
 * The API returns details: { existing_domain_id: '...' } on 409 DOMAIN_ALREADY_EXISTS.
 */
function extractExistingDomainId(error: unknown): string | null {
  if (error && typeof error === 'object') {
    const err = error as { details?: { existing_domain_id?: string }; code?: string }
    if (err.details?.existing_domain_id) return err.details.existing_domain_id
    if (err.code === 'DOMAIN_ALREADY_EXISTS') return null
  }
  return null
}

export function StepDomainFull({ onNext, onSkip }: StepDomainFullProps): React.JSX.Element {
  const [domain, setDomain] = useState('')
  const [existingDomainId, setExistingDomainId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY)
    } catch {
      return null
    }
  })

  const addDomain = useAddDomain()
  const deleteDomain = useDeleteDomain()
  const domainsList = useDomains()

  // Resolve domain ID: from addDomain response, or from existing/sessionStorage recovery
  const domainId = addDomain.data?.id ?? existingDomainId
  const detail = useDomainDetail(domainId ?? '', domainId !== null)

  // Auto-polling verification: enabled when we have a domain and it's not yet active
  const isVerifiedFromDetail = detail.data?.status === 'active'
  const autoVerify = useAutoVerifyDomain(domainId ?? '', domainId !== null && !isVerifiedFromDetail)
  const isVerified = isVerifiedFromDetail || autoVerify.data?.status === 'active'

  const records = detail.data?.records ?? addDomain.data?.records ?? []
  const domainName = detail.data?.domain ?? addDomain.data?.domain ?? domain

  // Persist domainId to sessionStorage for refresh recovery
  useEffect(() => {
    if (domainId) {
      try {
        sessionStorage.setItem(SESSION_KEY, domainId)
      } catch {
        /* quota */
      }
    }
  }, [domainId])

  // Auto-skip: if user already has a verified domain, advance immediately
  const hasVerifiedDomain = domainsList.data?.data.some((d) => d.status === 'active')
  const skipRef = useRef(false)
  useEffect(() => {
    if (hasVerifiedDomain && !skipRef.current) {
      skipRef.current = true
      onNext()
    }
  }, [hasVerifiedDomain, onNext])

  // When addDomain fails with "already exists", recover the existing domain
  useEffect(() => {
    if (!addDomain.isError) return

    const existingId = extractExistingDomainId(addDomain.error)
    if (existingId) {
      setExistingDomainId(existingId)
      return
    }

    // Fallback: search the domains list for a matching domain name
    const trimmed = domain.trim().toLowerCase()
    if (trimmed && domainsList.data?.data) {
      const match = domainsList.data.data.find((d) => d.domain === trimmed)
      if (match) setExistingDomainId(match.id)
    }
  }, [addDomain.isError, addDomain.error, domain, domainsList.data])

  function handleAdd(): void {
    if (!domain.trim()) return
    setExistingDomainId(null)
    addDomain.mutate({ domain: domain.trim(), region: 'eu' })
  }

  const handleCheckNow = useCallback(() => {
    autoVerify.refetch()
  }, [autoVerify])

  function handleChangeDomain(): void {
    // Delete the current domain from the backend first, then reset UI.
    // CASCADE will clean up domain_records automatically.
    if (domainId) {
      deleteDomain.mutate(domainId)
    }
    setExistingDomainId(null)
    addDomain.reset()
    setDomain('')
    try {
      sessionStorage.removeItem(SESSION_KEY)
    } catch {
      /* quota */
    }
  }

  // Show DNS records if we have a domain (either freshly created or recovered)
  const hasDomain = domainId !== null && records.length > 0

  /* ---- State 1: Domain input ---- */
  if (!hasDomain) {
    const showError = addDomain.isError && !existingDomainId
    return (
      <div className="rounded-2xl border border-surface-3 bg-surface-0 p-8 lg:p-10 shadow-sm">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-bold text-text-primary">Verify your domain</h2>
            <p className="text-sm text-text-secondary">Enter the domain you'll send emails from.</p>
          </div>
          <div className="flex gap-3">
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder="mail.yourapp.com"
              className="flex-1 rounded-lg border border-surface-3 bg-surface-0 px-4 py-2.5 text-sm
                text-text-primary placeholder:text-text-tertiary focus:border-brand-300
                focus:outline-none focus:ring-1 focus:ring-brand-300"
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={addDomain.isPending || !domain.trim()}
              className="shrink-0 rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white
                hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {addDomain.isPending ? 'Adding...' : 'Add domain'}
            </button>
          </div>
          <div className="rounded-lg border border-surface-3 bg-surface-1 px-4 py-3">
            <p className="text-sm text-text-secondary">
              We recommend using a subdomain like{' '}
              <span className="font-mono text-text-primary">mail.yourapp.com</span> or{' '}
              <span className="font-mono text-text-primary">send.yourapp.com</span>. This protects
              your root domain's reputation and makes DNS management easier.
            </p>
            <a
              href="https://docs.one-email.com/guides/subdomain-setup"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-block text-xs text-brand-600 hover:text-brand-700 hover:underline"
            >
              Learn more about subdomains for email
            </a>
          </div>
          {showError && (
            <p className="text-sm text-red-600">
              {addDomain.error instanceof Error ? addDomain.error.message : 'Failed to add domain'}
            </p>
          )}
          <div className="flex items-center">
            <button
              type="button"
              onClick={onSkip}
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    )
  }

  /* ---- State 2: DNS records + verification ---- */
  return (
    <DomainVerifyState
      domainId={domainId}
      domainName={domainName}
      records={records}
      isVerified={isVerified}
      autoVerify={autoVerify}
      onCheckNow={handleCheckNow}
      onChangeDomain={handleChangeDomain}
      onNext={onNext}
      onSkip={onSkip}
    />
  )
}
